import { closestCenter, DndContext } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type React from "react";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import Button from "~/components/ui/button";
import { SentenceParagraph } from "~/components/ui/SentenceParagraph";
import DeleteIcon from "~/images/delete_row.svg?react";
import DragDrop from "~/images/drag_drop.svg?react";
import type { SystemProposalData } from "~/slices/systemProposalSlice";
import docStyles from "../documentStyles.module.scss";
import styles from "./index.module.scss";

type ArrayField =
  | "その他"
  | "メリット"
  | "導入するポイント"
  | "特徴"
  | "業務フロー"
  | "目的"
  | "背景";

interface FormattedRow {
  id: string;
  [key: string]: string;
}

export interface SystemProposalDisplayProps {
  data: SystemProposalData;
  isEditable?: boolean;
  noWrapper?: boolean;
  isComparisonMode?: boolean;
  comparisonData?: { content?: string };
  onArrayFieldChange?: (field: ArrayField, value: string[]) => void;
  onTableChange?: (
    field: "ユースケーステーブル" | "主な機能",
    value: { headers: string[]; rows: string[][] },
  ) => void;
  onAddUsecaseRow?: () => void;
  onDeleteUsecaseRow?: (index: number) => void;
  onAddMainFunctionRow?: () => void;
  onDeleteMainFunctionRow?: (index: number) => void;
}

const SortableItem = ({
  id,
  children,
  isEditable,
}: {
  id: string;
  children: React.ReactNode;
  isEditable: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform) };

  return (
    <tr ref={setNodeRef} style={style}>
      {isEditable && (
        <td {...listeners} {...attributes} style={{ cursor: "grab" }}>
          <DragDrop width={16} height={16} />
        </td>
      )}
      {children}
    </tr>
  );
};

interface EditableTableProps {
  data: Record<string, string>[];
  columns: ColumnDef<Record<string, string>>[];
  onChange: (data: Record<string, string>[]) => void;
  onAddRow: () => void;
  onDeleteRow: (rowIndex: number) => void;
  isEditable: boolean;
  comparisonData?: Record<string, string>[];
  isComparisonMode?: boolean;
}

const EditableTable: React.FC<EditableTableProps> = ({
  data,
  columns,
  onChange,
  onAddRow,
  onDeleteRow,
  isEditable,
  comparisonData = [],
  isComparisonMode = false,
}) => {
  const handleInputChange = (rowIndex: number, columnId: string, value: string) => {
    const updatedData = [...data];
    updatedData[rowIndex]![columnId] = value;
    onChange(updatedData);
  };

  const isDifferent = (rowIndex: number, columnId: string): boolean => {
    if (isComparisonMode && comparisonData.length > 0) {
      const value1 = data[rowIndex]?.[columnId];
      const value2 = comparisonData[rowIndex]?.[columnId];
      if (value1 !== undefined && value2 !== undefined) {
        return value1.localeCompare(value2) !== 0;
      }
    }
    return false;
  };

  const table = useReactTable({
    data,
    columns: [
      ...columns,
      {
        accessorKey: "delete",
        header: "",
        cell: ({ row }) =>
          isEditable ? (
            <button
              type="button"
              onClick={() => onDeleteRow(row.index)}
              style={{ cursor: "pointer", justifyContent: "flex-end" }}
            >
              <DeleteIcon width={20} height={20} />
            </button>
          ) : null,
      },
    ],
    getCoreRowModel: getCoreRowModel(),
  });

  const implementationOptions = [
    "基本機能",
    "プラグイン",
    "カスタマイズ",
    "外部連携",
    "外部サービス",
  ];

  // 既存データの「標準機能」を「基本機能」に表示マッピング
  const normalizeImplementationMethod = (value: string): string =>
    value === "標準機能" ? "基本機能" : value;

  return (
    <>
      <table className={`${styles["table"]} ${isEditable ? styles["is-editable"] : ""}`}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr className={styles["table-tr"]} key={headerGroup.id}>
              {isEditable && <th></th>}
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {isEditable ? (
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (active && over && active.id !== over.id) {
                  const oldIndex = table
                    .getRowModel()
                    .rows.findIndex((item) => item.id === active.id);
                  const newIndex = table
                    .getRowModel()
                    .rows.findIndex((item) => item.id === over.id);

                  if (oldIndex !== -1 && newIndex !== -1) {
                    const updatedData = arrayMove(data, oldIndex, newIndex);
                    onChange(updatedData.map(({ id, ...row }) => row));
                  }
                }
              }}
            >
              <SortableContext
                items={table.getRowModel().rows.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <SortableItem key={row.id} id={row.id} isEditable={isEditable}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        className={`${styles["table-td"]} ${
                          isDifferent(rowIndex, cell.id as string) ? styles["diff-highlight"] : ""
                        }`}
                        key={cell.id}
                      >
                        {cell.column.id === "delete" ? (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        ) : isEditable ? (
                          cell.column.id === "実装方法" ? (
                            <select
                              value={normalizeImplementationMethod(String(cell.getValue() || ""))}
                              onChange={(e) =>
                                handleInputChange(rowIndex, cell.column.id, e.target.value)
                              }
                            >
                              {implementationOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <textarea
                              value={String(cell.getValue() || "")}
                              onChange={(e) =>
                                handleInputChange(rowIndex, cell.column.id, e.target.value)
                              }
                              className={styles["auto-resize"]}
                              readOnly={!isEditable}
                              rows={1}
                              style={{
                                width: "100%",
                                resize: "none",
                              }}
                            />
                          )
                        ) : (
                          <div>
                            {cell.column.id === "実装方法"
                              ? normalizeImplementationMethod(cell.getValue() as string)
                              : (cell.getValue() as string)}
                          </div>
                        )}
                      </td>
                    ))}
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            table.getRowModel().rows.map((row, rowIndex) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={
                      isDifferent(rowIndex, cell.id as string) ? styles["diff-highlight"] : ""
                    }
                  >
                    {cell.column.id === "delete" ? (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    ) : (
                      <div>
                        {cell.column.id === "実装方法"
                          ? normalizeImplementationMethod(cell.getValue() as string)
                          : (cell.getValue() as string)}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {isEditable && (
        <div>
          <Button onClick={onAddRow} label="追加" styleType="add-row" icon="addRowIcon" />
        </div>
      )}
    </>
  );
};

const tryParseJSON = (content: string | undefined): SystemProposalData | undefined => {
  if (!content) return undefined;
  if (!content.trim().startsWith("{")) return undefined;
  try {
    return JSON.parse(content);
  } catch {
    return undefined;
  }
};

const formatTableData = (headers: string[], rows: string[][]): FormattedRow[] => {
  if (!rows || !Array.isArray(rows) || !headers || !Array.isArray(headers)) {
    return [];
  }
  return rows.map((row: string[], rowIndex: number) =>
    row.reduce(
      (acc, value, index) => {
        acc[headers[index]!] = value;
        return acc;
      },
      { id: `row-${rowIndex}` } as FormattedRow,
    ),
  );
};

const denormalizeTableData = (tableData: Record<string, string>[], headers: string[]) => {
  return tableData.map((row) => {
    return headers.map((header) => row[header] || "");
  });
};

const SystemProposalDisplay: React.FC<SystemProposalDisplayProps> = ({
  data,
  isEditable = false,
  noWrapper = false,
  isComparisonMode = false,
  comparisonData,
  onArrayFieldChange,
  onTableChange,
  onAddUsecaseRow,
  onDeleteUsecaseRow,
  onAddMainFunctionRow,
  onDeleteMainFunctionRow,
}) => {
  const [formattedUsecaseData, formattedFunctionsData] = useMemo(() => {
    const fk = Object.keys(data)[0];
    if (!fk) return [[], []] as [FormattedRow[], FormattedRow[]];

    const systemOverview = data[fk]!.システム概要;
    if (!systemOverview) return [[], []] as [FormattedRow[], FormattedRow[]];

    const usecaseTable = systemOverview.ユースケーステーブル;
    const usecaseData = formatTableData(usecaseTable?.headers || [], usecaseTable?.rows || []);

    const functionsTable = systemOverview.主な機能;
    const functionsData = formatTableData(
      functionsTable?.headers || [],
      functionsTable?.rows || [],
    );

    return [usecaseData, functionsData] as [FormattedRow[], FormattedRow[]];
  }, [data]);

  const [comparisonUsecaseData, comparisonFunctionsData] = useMemo(() => {
    if (!comparisonData?.content) return [[], []] as [FormattedRow[], FormattedRow[]];

    const parsedComparisonData = tryParseJSON(comparisonData.content);
    if (!parsedComparisonData) return [[], []] as [FormattedRow[], FormattedRow[]];

    const fk = Object.keys(parsedComparisonData)[0];
    if (!fk) return [[], []] as [FormattedRow[], FormattedRow[]];

    const systemOverview = parsedComparisonData[fk]?.システム概要;
    if (!systemOverview) return [[], []] as [FormattedRow[], FormattedRow[]];

    const usecaseTable = systemOverview.ユースケーステーブル;
    const usecaseData = formatTableData(usecaseTable?.headers || [], usecaseTable?.rows || []);

    const functionsTable = systemOverview.主な機能;
    const functionsData = formatTableData(
      functionsTable?.headers || [],
      functionsTable?.rows || [],
    );
    return [usecaseData, functionsData] as [FormattedRow[], FormattedRow[]];
  }, [comparisonData?.content]);

  const firstKey = Object.keys(data)[0];

  if (!firstKey || !data[firstKey]?.システム概要) {
    return <div>データ形式が不正です</div>;
  }

  const currentSystemData = data[firstKey]!;
  const currentOverview = currentSystemData.システム概要;

  const usecaseColumns: ColumnDef<Record<string, string>>[] = [
    { accessorKey: "ユースケース", header: "ユースケース" },
    { accessorKey: "ユースケース群", header: "ユースケース群" },
    { accessorKey: "アクター", header: "アクター" },
    { accessorKey: "使用サービス", header: "使用サービス" },
    { accessorKey: "実装方法", header: "実装方法" },
  ];

  const functionsColumns: ColumnDef<Record<string, string>>[] = [
    { accessorKey: "機能", header: "機能" },
    { accessorKey: "概要", header: "概要" },
  ];

  const handleUsecaseTableChange = (updatedTable: Record<string, string>[]) => {
    const headers = data[firstKey]!.システム概要.ユースケーステーブル.headers;
    onTableChange?.("ユースケーステーブル", {
      headers,
      rows: denormalizeTableData(updatedTable, headers),
    });
  };

  const handleFunctionsTableChange = (updatedTable: Record<string, string>[]) => {
    const headers = data[firstKey]!.システム概要.主な機能.headers;
    onTableChange?.("主な機能", {
      headers,
      rows: denormalizeTableData(updatedTable, headers),
    });
  };

  const renderMarkdownField = (field: ArrayField, value: string[], comparisonValue?: string[]) => {
    const handleArrayChange = (index: number, newValue: string) => {
      if (Array.isArray(value)) {
        const updatedValue = [...value];
        updatedValue[index] = newValue;
        onArrayFieldChange?.(field, updatedValue);
      }
    };

    const deleteArrayItem = (index: number) => {
      if (Array.isArray(value)) {
        const updatedValue = value.filter((_, i) => i !== index);
        onArrayFieldChange?.(field, updatedValue);
      }
    };

    const addNewArrayItem = () => {
      if (Array.isArray(value)) {
        onArrayFieldChange?.(field, [...value, ""]);
      }
    };

    if (Array.isArray(value)) {
      return (
        <div className={styles["markdown-container"]}>
          {!isEditable ? (
            <ReactMarkdown>{value.map((item) => `- ${item}`).join("\n")}</ReactMarkdown>
          ) : (
            <>
              {value.map((item, index) => {
                const comparisonItem = comparisonValue?.[index];
                const highlightClass =
                  isComparisonMode && comparisonItem !== undefined && item !== comparisonItem
                    ? styles["diff-highlight"]
                    : "";

                return (
                  <div
                    key={`${field}-${index}`}
                    className={`${styles["textarea-wrapper"]} ${highlightClass}`}
                  >
                    <textarea
                      className={styles["textarea"]}
                      value={item}
                      onChange={(e) => handleArrayChange(index, e.target.value)}
                      rows={1}
                    />
                    <button type="button" onClick={() => deleteArrayItem(index)}>
                      <DeleteIcon />
                    </button>
                  </div>
                );
              })}
              <Button
                onClick={addNewArrayItem}
                label="追加"
                styleType="add-row"
                icon="addRowIcon"
              />
            </>
          )}
        </div>
      );
    }
    return <div>不正なデータ形式です。</div>;
  };

  const content = (
    <div className={docStyles["document-content"]} data-testid="document-view">
      <h1>{firstKey || ""}</h1>

      <h2>システム概要</h2>

      {currentOverview.概要 && (
        <>
          <h3>概要</h3>
          <SentenceParagraph text={currentOverview.概要} className={styles["markdown-container"]} />
        </>
      )}

      {currentOverview.背景?.length > 0 && (
        <>
          <h3>背景</h3>
          {renderMarkdownField(
            "背景",
            currentOverview.背景,
            comparisonData?.content
              ? tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.背景
              : undefined,
          )}
        </>
      )}

      <h3>システム導入の目的</h3>
      {renderMarkdownField(
        "目的",
        currentOverview.目的,
        comparisonData?.content
          ? tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.目的
          : undefined,
      )}

      <h3>主な機能</h3>
      <EditableTable
        data={formattedFunctionsData}
        columns={functionsColumns}
        onChange={handleFunctionsTableChange}
        onAddRow={() => onAddMainFunctionRow?.()}
        onDeleteRow={(rowIndex) => onDeleteMainFunctionRow?.(rowIndex)}
        isEditable={isEditable}
        {...(comparisonFunctionsData && { comparisonData: comparisonFunctionsData })}
        isComparisonMode={isComparisonMode}
      />

      <h3>業務フロー</h3>
      {renderMarkdownField(
        "業務フロー",
        currentOverview.業務フロー,
        comparisonData?.content
          ? tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.業務フロー
          : undefined,
      )}

      <h3>{currentOverview.特徴 ? "特徴" : currentOverview.メリット ? "メリット" : "特徴"}</h3>
      {renderMarkdownField(
        currentOverview.特徴 ? "特徴" : currentOverview.メリット ? "メリット" : "特徴",
        currentOverview.特徴 ?? currentOverview.メリット ?? currentOverview.導入するポイント ?? [],
        comparisonData?.content
          ? (tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.特徴 ??
              tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.メリット ??
              tryParseJSON(comparisonData.content)?.[firstKey!]?.システム概要?.導入するポイント)
          : undefined,
      )}

      <h2>ユースケーステーブル</h2>
      <EditableTable
        data={formattedUsecaseData}
        columns={usecaseColumns}
        onChange={handleUsecaseTableChange}
        onAddRow={() => onAddUsecaseRow?.()}
        onDeleteRow={(rowIndex) => onDeleteUsecaseRow?.(rowIndex)}
        isEditable={isEditable}
        {...(comparisonUsecaseData && { comparisonData: comparisonUsecaseData })}
        isComparisonMode={isComparisonMode}
      />
    </div>
  );

  if (noWrapper) {
    return content;
  }

  return (
    <div className={docStyles["wrapper"]}>
      <div className={docStyles["innerDiv"]}>{content}</div>
    </div>
  );
};

export default SystemProposalDisplay;
